import { Link } from "react-router-dom";
import { CheckCircle2, Video, CalendarPlus, ShieldCheck, Clock, MessageCircle } from "lucide-react";
import ConsultationForm from "@/components/ConsultationForm";
import { ASSET, CONSULTATION, WHATSAPP_URL } from "@/constants/testIds";

const AGENDA = [
  "Live walkthrough of the Chapter III plot layout & inventory",
  "Pricing, payment plan and current launch offers",
  "RERA registration, NA conversion & 7/12 extract — line by line",
  "Connectivity map: Mumbai 3.0, Missing Link, Navi Mumbai International Airport drive-times",
  "Your family situation, budget & timeline — off-the-record",
  "Clear next step — visit, defer, or step away, on your terms",
];

const WHY_ONLINE = [
  {
    icon: Video,
    title: "Live, one-on-one",
    body: "A senior sales advisor on Google Meet — screen-share of the plot map, not a scripted deck.",
  },
  {
    icon: CalendarPlus,
    title: "Straight to your Google Calendar",
    body: "Book your slot, we add the meeting to your calendar instantly with a Meet link included.",
  },
  {
    icon: Clock,
    title: "30 focused minutes",
    body: "Not a webinar. Not a queue. Your questions answered in the order that matters to you.",
  },
  {
    icon: ShieldCheck,
    title: "Zero pressure",
    body: "No automated follow-up loops. If we're not the right fit, we'll tell you honestly.",
  },
];

export default function StrategyCall() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[80vh] bg-void overflow-hidden">
        <img
          src={ASSET.heroSarasgad}
          alt="Sarasgad ridge at golden hour"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-transparent" />
        <div className="relative container-editorial px-5 md:px-10 pt-32 md:pt-40 pb-16 md:pb-24 text-cream">
          <div className="max-w-4xl fade-up">
            <div className="eyebrow text-lime mb-6 inline-flex items-center gap-2">
              <span className="editorial-rule bg-lime" />
              Free · 30-min Google Meet · Instant Google Calendar
            </div>
            <h1 className="font-display text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98]">
              Book an Online<br />
              <span className="italic text-cream/95">Consultation.</span>
            </h1>
            <p className="mt-8 text-cream/85 text-base md:text-lg max-w-2xl leading-relaxed">
              A 30-minute one-on-one video call with our senior sales advisor.
              Pick a slot that suits you — we'll add it straight to your Google
              Calendar with a Meet link included.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-cream/85">
              <span className="inline-flex items-center gap-2">
                <Video size={16} className="text-lime" /> Google Meet
              </span>
              <span className="inline-flex items-center gap-2">
                <CalendarPlus size={16} className="text-lime" /> Auto-added to Google Calendar
              </span>
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={16} className="text-lime" /> No follow-up spam
              </span>
            </div>

            <div className="mt-10">
              <a href="#book" className="btn-primary">
                <Video size={16} /> Book my consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY ONLINE */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial px-5 md:px-10 grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-4">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> Why Online First
            </div>
            <h2 className="font-display text-forest text-4xl md:text-5xl leading-tight">
              Sort the questions<br />before the drive.
            </h2>
            <p className="mt-6 text-forest/70 leading-relaxed max-w-md">
              Pali is a two-hour trip. Save it for when you're ready to walk the
              land — not for questions we can answer on video in 30 minutes.
            </p>
          </aside>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {WHY_ONLINE.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  className="bg-white border border-forest/10 rounded-xl p-6 md:p-7 space-y-3 hover:border-forest/40 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-forest text-lime grid place-items-center">
                    <Icon size={18} />
                  </div>
                  <h3 className="font-display text-forest text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-forest/70 text-[14px] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AGENDA + FORM */}
      <section id="book" className="section-pad bg-cream">
        <div className="container-editorial px-5 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> The Agenda
            </div>
            <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
              What we cover<br />in {CONSULTATION.duration} minutes.
            </h2>
            <ul className="mt-8 space-y-3">
              {AGENDA.map((a) => (
                <li key={a} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-teal mt-0.5 shrink-0" />
                  <span className="text-forest/85 text-[15px] leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 border-l-2 border-lime pl-5 py-2">
              <div className="eyebrow text-forest/50">You leave with</div>
              <div className="font-display text-forest text-xl mt-1 leading-tight">
                A calendar invite, a Meet link, and a clear next step.
              </div>
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="bg-white border border-forest/10 rounded-2xl p-6 md:p-8 shadow-[0_20px_60px_-30px_rgba(11,74,61,0.35)]">
              <div className="eyebrow text-teal">Free · No obligation</div>
              <h3 className="font-display text-forest text-2xl md:text-3xl mt-2 leading-tight">
                Pick your slot
              </h3>
              <p className="text-sm text-forest/60 mt-2">
                All times are IST. Our advisor confirms the exact meeting link 30 minutes before.
              </p>
              <div className="mt-6">
                <ConsultationForm source="consultation_page" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative paths */}
      <section className="section-pad bg-forest text-cream">
        <div className="container-editorial px-5 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div>
            <div className="eyebrow text-lime mb-4">
              <span className="editorial-rule bg-lime" /> Prefer another route?
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight max-w-2xl">
              Site visit at golden hour,<br />or a quick WhatsApp reply?
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn-outline-cream">Book a Site Visit →</Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="btn-outline-cream">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
